import { chainsMetadata } from "data/rpc";
import { BaseTxButton, Step, StepTxLink, useWeb3 } from "logic/components";
import { useContractView, useDebounceCallback } from "logic/hooks";
import { usePrivyUserMetadata } from "logic/hooks/privy/usePrivyUserMetadata";
import { ERC20V2 } from "logic/pages/LoansPage/data/types";
import { APP_TOKENS, fromUnits, printNumber, toUnits } from "logic/utils";
import { CHAINS_PEER } from "logic/utils";
import { useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Button, Input, Modal, Select, Skeleton, type ModalBaseProps, type SelectOption } from "ui/components";
import { Address, erc20Abi, isAddress, isAddressEqual, zeroAddress } from "viem";

import { useWithdrawTx } from "./useWithdrawTx";

type TokenType = "pyusd" | "usdai";

const TOKEN_OPTIONS: SelectOption[] = [
  { value: "pyusd", label: "PYUSD", icon: "/svg/erc20/pyusd.svg" },
  { value: "usdai", label: "USDai", icon: "/svg/erc20/usdai.svg" },
];

type Props = ModalBaseProps & {
  token: ERC20V2;
  chainId: number;
};

export function WithdrawModal(props: Props) {
  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose}>
      <Modal.Title>Withdraw Funds</Modal.Title>
      <ErrorBoundary
        FallbackComponent={({ error }) => (
          <div className="h-96 flex items-center flex-col gap-1 justify-center">
            <span className="text-sm text-secondary">Something went wrong</span>
            {error instanceof Error && <span className="text-sm text-secondary">({error.message})</span>}
          </div>
        )}
      >
        <WithdrawModal_ {...props} />
      </ErrorBoundary>
    </Modal>
  );
}
function WithdrawModal_(props: Props) {
  const { chainId, onClose } = props;

  const [tokenType, setTokenType] = useState<TokenType>("pyusd");
  const [inputChainId, setInputChainId] = useState(chainId);
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState("");
  const [destinationChainId, setDestinationChainId] = useState(chainId);

  const appTokens = APP_TOKENS[chainId];
  const isMainChain = inputChainId === chainId;
  const pyusdMeta = isMainChain ? appTokens?.pyusd.main : appTokens?.pyusd.peer;
  const usdaiMeta = isMainChain ? appTokens?.usdai.main : appTokens?.usdai.peer;
  const activeTokenMeta = tokenType === "usdai" ? usdaiMeta : pyusdMeta;
  const isUsdaiUnsupported = tokenType === "usdai" && !usdaiMeta;

  const activeToken: ERC20V2 = activeTokenMeta
    ? { address: activeTokenMeta.address, symbol: activeTokenMeta.symbol, decimals: activeTokenMeta.decimals }
    : props.token;

  const amountUnits = toUnits(amount || "0", activeToken.decimals);

  const inputChainOptions: SelectOption[] = useMemo(() => {
    const options: SelectOption[] = [
      {
        value: String(chainId),
        label: chainsMetadata[chainId]?.name ?? "Main Chain",
        icon: chainsMetadata[chainId]?.logoUrl,
      },
    ];
    const peerChainId = CHAINS_PEER[chainId];
    if (peerChainId) {
      options.push({
        value: String(peerChainId),
        label: chainsMetadata[peerChainId]?.name ?? "Peer Chain",
        icon: chainsMetadata[peerChainId]?.logoUrl,
      });
    }
    return options;
  }, [chainId]);

  const destinationChainOptions: SelectOption[] = useMemo(() => {
    const options: SelectOption[] = [
      {
        value: String(inputChainId),
        label: chainsMetadata[inputChainId]?.name ?? "Same Chain",
        icon: chainsMetadata[inputChainId]?.logoUrl,
      },
    ];
    const peerChainId = CHAINS_PEER[inputChainId];
    if (peerChainId) {
      options.push({
        value: String(peerChainId),
        label: chainsMetadata[peerChainId]?.name ?? "Peer Chain",
        icon: chainsMetadata[peerChainId]?.logoUrl,
      });
    }
    return options;
  }, [inputChainId]);

  const { action, isDone, steps, currentStep, currentStepIndex } = useWithdrawTx({
    amount: amountUnits,
    recipient,
    token: activeToken,
    chainId: inputChainId,
    destinationChainId,
    enabled: !isUsdaiUnsupported,
  });
  const isPending = currentStep.status == "sending" || currentStep.status == "confirming";

  const [{ addressError, amountError }, setErrors] = useState<{
    addressError: string | undefined;
    amountError: string | undefined;
  }>({ addressError: undefined, amountError: undefined });

  const debouncedSetErrors = useDebounceCallback(setErrors, 500);

  useEffect(() => {
    debouncedSetErrors({
      addressError: recipient && !isAddress(recipient) ? "Invalid address" : undefined,
      amountError: amount && (isNaN(Number(amount)) || Number(amount) <= 0) ? "Invalid amount" : undefined,
    });
  }, [recipient, amount, debouncedSetErrors]);

  const onTokenTypeChange = (val: string) => {
    setTokenType(val as TokenType);
    setRecipient("");
    setAmount("");
    setDestinationChainId(inputChainId);
  };

  const onInputChainChange = (val: string) => {
    const newInputChainId = Number(val);
    setInputChainId(newInputChainId);
    setRecipient("");
    setAmount("");
    setDestinationChainId(newInputChainId);
  };

  const canSubmit = Boolean(action.send && amountUnits && recipient && !addressError && !amountError);

  const onSubmit = () => {
    if (!canSubmit) return;
    if (action.send) {
      action.send();
    }
  };

  return (
    <Modal.Children className="gap-1">
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <label className="text-sm text-secondary">Token</label>
          <Select
            triggerClassName="border border-outline-major bg-white"
            value={tokenType}
            onChange={onTokenTypeChange}
            options={TOKEN_OPTIONS}
            disabled={isPending}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm text-secondary">Source Chain</label>
          <Select
            triggerClassName="border border-outline-major bg-white"
            value={String(inputChainId)}
            onChange={onInputChainChange}
            options={inputChainOptions}
            disabled={inputChainOptions.length <= 1 || isPending}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm text-secondary">Destination Chain</label>
          <Select
            triggerClassName="border border-outline-major bg-white"
            value={String(destinationChainId)}
            onChange={(val) => setDestinationChainId(Number(val))}
            options={destinationChainOptions}
            disabled={destinationChainOptions.length <= 1 || isPending}
          />
        </div>
        {isUsdaiUnsupported ? (
          <p className="text-sm text-red-500">USDai is not available on this chain.</p>
        ) : (
          <WithdrawForm
            token={activeToken}
            chainId={inputChainId}
            recipient={recipient}
            amount={amount}
            addressError={addressError}
            amountError={amountError}
            setRecipient={setRecipient}
            setAmount={setAmount}
          />
        )}
        <div className="w-full">
          {steps.map((step, index) => (
            <Step
              stepContent={<StepTxLink chainId={inputChainId} step={step} />}
              key={index}
              step={step}
              index={index}
              currentStepIndex={currentStepIndex}
            />
          ))}
        </div>
      </div>
      <BaseTxButton
        disabled={!canSubmit}
        isLoading={isPending}
        isDone={isDone}
        onDone={() => onClose?.(false)}
        onClick={onSubmit}
      >
        {currentStep.action}
      </BaseTxButton>
    </Modal.Children>
  );
}

interface WithdrawFormProps {
  recipient: string;
  amount: string;
  addressError: string | undefined;
  amountError: string | undefined;
  setRecipient: (recipient: string) => void;
  setAmount: (amount: string) => void;
  token: ERC20V2;
  chainId: number;
}

function WithdrawForm(props: WithdrawFormProps) {
  const { recipient, amount, setRecipient, setAmount, token, chainId } = props;

  const { address: connectedAddress = zeroAddress } = useWeb3();
  const { linkedWallets } = usePrivyUserMetadata();

  const wallet = linkedWallets.find(
    (w) => connectedAddress !== zeroAddress && isAddressEqual(w.address, connectedAddress),
  );
  const recipients = useMemo(() => wallet?.recipients ?? [], [wallet]);

  const { data: balance } = useContractView({
    address: token.address,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: [connectedAddress as Address],
    chainId,
    query: {
      enabled: Boolean(connectedAddress !== zeroAddress),
    },
  });

  return (
    <div className="flex flex-col gap-4 w-full">
      <RecipientSelect recipients={recipients} value={recipient} onChange={setRecipient} />
      <div className="flex flex-col gap-2">
        <label className="text-sm text-secondary">Amount</label>
        <Input type="number" value={amount} onChange={setAmount} placeholder="0.0" />
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-secondary">
            Balance:{" "}
            {balance !== undefined ? (
              `$${printNumber(fromUnits(balance, token.decimals))}`
            ) : (
              <Skeleton className="w-10 h-4" />
            )}
          </span>
          <Button
            disabled={balance === undefined}
            onClick={() => setAmount(fromUnits(balance ?? 0n, token.decimals).toString())}
            className="btn-xs btn-tertiary"
          >
            Max
          </Button>
        </div>
      </div>
    </div>
  );
}

interface RecipientSelectProps {
  recipients: Address[];
  value: string;
  onChange: (value: string) => void;
}

function RecipientSelect({ recipients, value, onChange }: RecipientSelectProps) {
  const isEmpty = recipients.length === 0;
  const isSingle = recipients.length === 1;

  useEffect(() => {
    if (isSingle && value !== recipients[0]) {
      onChange(recipients[0]);
    }
  }, [isSingle, recipients, value, onChange]);

  const options: SelectOption[] = recipients.map((address) => ({
    value: address,
    label: address,
  }));

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm text-secondary">Whitelisted Recipient {`Address${isSingle ? "" : "es"}`}:</label>
      <Select
        triggerClassName="border border-outline-major bg-white"
        value={value}
        onChange={onChange}
        options={options}
        placeholder={isEmpty ? "No whitelisted recipients" : "Select recipient"}
        disabled={isEmpty || isSingle}
      />
    </div>
  );
}
